import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";

import {
  searchPersonsByProfile,
  getPersonsByIdsQuery,
  getPersonByIdQuery,
  getPersonBySlugQuery,
  isManagerOfPersonQuery,
  getHierarchyEmailAddressesQuery,
} from "./queries";
import { useUserContext } from "api/user/UserProvider";
import { setListUniqByIdHelper, withUserScopeCheck } from "utils/helper";

const PersonContext = createContext({});

export const usePersonContext = () => useContext(PersonContext);

export const PersonProvider = ({ ...props }) => {
  const [personsByProfileList, setPersonsByProfileList] = useState([]);
  const [personsList, setPersonsList] = useState([]);
  const [personById, setPersonById] = useState({});
  const [personBySlug, setPersonBySlug] = useState({});
  const [actualManager, setActualManager] = useState({});
  const [hierarchyEmailAddresses, setHierarchyEmailAddresses] = useState({});
  const { userScope } = useUserContext();

  const isManagerOfPerson = useCallback(async ({ personId, managerId }) => {
    try {
      const result = await isManagerOfPersonQuery(personId, managerId);
      setActualManager(result);
      return result;
    } catch (e) {
      console.error("isManagerOfPerson", e);
      throw e;
    }
  }, []);

  const getPersonBySlug = useCallback(
    async (slug) => {
      try {
        await withUserScopeCheck({
          callback: async () => {
            setPersonBySlug(await getPersonBySlugQuery(slug, userScope));
          },
          scope: userScope,
        });
      } catch (e) {
        console.error("getPersonBySlug", e);
        throw e;
      }
    },
    [userScope]
  );

  const getPersonsByProfile = useCallback(
    async ({ profile, search }) => {
      try {
        await withUserScopeCheck({
          callback: async () => {
            const result = await searchPersonsByProfile({
              profile,
              search,
              userScope,
            });
            setPersonsList((old) => setListUniqByIdHelper(old, result));
            setPersonsByProfileList((old) =>
              setListUniqByIdHelper(old, result)
            );
          },
          scope: userScope,
        });
      } catch (e) {
        console.error("getPersonsByProfile", e);
        throw e;
      }
    },
    [userScope]
  );

  const getPersonsByIds = useCallback(
    async ({ ids }) => {
      try {
        await withUserScopeCheck({
          callback: async () => {
            if (ids) {
              const result = await getPersonsByIdsQuery({
                ids,
                userScope,
              });
              setPersonsList((old) => setListUniqByIdHelper(old, result));
            }
          },
          scope: userScope,
        });
      } catch (e) {
        console.error("getPersonsByIds", e);
        throw e;
      }
    },
    [userScope]
  );

  const getPersonById = useCallback(
    async (id) => {
      try {
        await withUserScopeCheck({
          callback: async () => {
            return setPersonById(await getPersonByIdQuery(id, userScope));
          },
          scope: userScope,
        });
      } catch (e) {
        console.error("getPersonById", e);
        throw e;
      }
    },
    [userScope]
  );

  const getHierarchyEmailAddresses = useCallback(
    async ({ entities }) => {
      try {
        entities?.length > 0 &&
          (await withUserScopeCheck({
            callback: async () => {
              const result = await getHierarchyEmailAddressesQuery({
                userScope,
                entities,
              });
              setHierarchyEmailAddresses(result);
            },
            scope: userScope,
          }));
      } catch (e) {
        console.error("getPersonBySlug", e);
        throw e;
      }
    },
    [userScope]
  );

  const values = useMemo(
    () => ({
      getPersonById,
      getPersonsByIds,
      getPersonsByProfile,
      personById,
      personsList,
      getPersonBySlug,
      personBySlug,
      personsByProfileList,
      setPersonsByProfileList,
      isManagerOfPerson,
      actualManager,
      hierarchyEmailAddresses,
      getHierarchyEmailAddresses,
      setPersonsList,
    }),
    [
      isManagerOfPerson,
      actualManager,
      personsByProfileList,
      getPersonBySlug,
      personBySlug,
      getPersonById,
      getPersonsByIds,
      getPersonsByProfile,
      personById,
      personsList,
      hierarchyEmailAddresses,
      getHierarchyEmailAddresses,
      setPersonsList,
    ]
  );
  return <PersonContext.Provider value={values} {...props} />;
};
